import {
  useState,
  createContext,
  useContext,
  useEffect,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
} from 'react';
import { useColumnSelect } from '@msrceng/component';
import { DownloadPanel } from './DownloadPanel/DownloadPanel';
import { PageControl } from 'component/context/util/pageControl';
import { ISearchListColumn } from 'component/Search/types';
import { useDownloadRecordRequestEffect } from './useDownloadRecordRequestEffect';
import { DownloadFileType } from './types';
import { saveOptionList } from './saveOptionList';

/** row counts larger than this number will get a warning message about how long the dl might take */
const sizeToShowLargeRowCountWarning = 2500;
/** how long to wait between fetching pages while downloading the whole table */
const pageDelayMsec = 400;

const context = createContext<IUseDownloadData>({} as IUseDownloadData);

export const DownloadProvider = <T,>({
  children,
  disabled = false,
  items = [],
  tableName,
  pageControl,
}: PropsWithChildren<IDownloadProviderProps<T>>) => {
  //hooks/state
  const [open, setOpen] = useState(false);
  const { selectedColumnList } = useColumnSelect<ISearchListColumn>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [fileType, setFileType] = useState<DownloadFileType>(
    DownloadFileType.xlsx
  );
  const [delay, setDelay] = useState<NodeJS.Timeout>();

  //helper vars
  /** Is the user requesting a large row count? */
  const isLarge =
    !!pageControl?.rowCount &&
    pageControl.rowCount > sizeToShowLargeRowCountWarning;
  /** What is our percent complete? */
  const percent = pageControl?.rowCount
    ? items?.length / pageControl?.rowCount
    : -1;
  /** Have we downloaded the whole dataset already? */
  const downloadComplete = isDownloading && !pageControl?.hasNextPage;
  /** Are we ready for the next page of the data? */
  const isReadyForNext =
    pageControl?.hasNextPage && !pageControl?.isLoading && !delay;

  useDownloadRecordRequestEffect({
    isDownloading,
    downloadComplete,
    rowCount: pageControl?.rowCount,
    fileType,
    tableName,
  });

  useEffect(() => {
    // This causes the next page to be requested every time
    // the timer is satisfied and there is a next page to request.
    if (isDownloading && isReadyForNext) {
      setDelay(
        setTimeout(() => {
          if (pageControl?.fetchNextPage) {
            pageControl.fetchNextPage();
          }
          setDelay(undefined);
        }, pageDelayMsec)
      );
    }
  }, [pageControl, isReadyForNext, isDownloading, delay]);

  // This handles the effects of the download completing.
  useEffect(() => {
    if (downloadComplete) {
      setIsDownloading(false);
      saveOptionList[fileType].onComplete(selectedColumnList, items);
    }
  }, [downloadComplete, fileType, items, selectedColumnList]);

  return (
    <context.Provider
      value={{
        percent,
        isDownloading,
        setIsDownloading,
        isLarge,
        fileType,
        setFileType,
        disabled,
        tableName,
        open,
        setOpen,
      }}
    >
      {children}
      <DownloadPanel />
    </context.Provider>
  );
};

/**
 * Hook to get the download context (for use in the download panel).
 */
export const useDownloadData = (): IUseDownloadData => useContext(context);

export interface IDownloadProviderProps<T> {
  /** The selected columns to include in the download */
  columns: ISearchListColumn<T>[];
  /** Disables downloading (and hides the button) */
  disabled: boolean;
  /** The name of the table being downloaded */
  tableName: string;
  /** The list of rows of data */
  items: T[];
  /** The page control for the table */
  pageControl?: PageControl;
}
export interface IUseDownloadData {
  /**
   * The percent complete of the download.
   */
  percent: number;
  isLarge: boolean;
  fileType: DownloadFileType;
  isDownloading: boolean;
  disabled: boolean;
  tableName: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setFileType: Dispatch<SetStateAction<DownloadFileType>>;
  setIsDownloading: Dispatch<SetStateAction<boolean>>;
}
